import { useCallback, useEffect, useRef, useState } from 'react'

// Generic, transport-agnostic chat transcript engine. It owns the message
// window and knows how to page in both directions, jump to an arbitrary message,
// and apply optimistic updates — but it knows nothing about searchrooms. Feed it
// an `api` adapter:
//
//   api.listPage({ limit, before, after, around }) ->
//       Promise<{ messages, hasMoreBefore, hasMoreAfter }>  (messages oldest-first)
//
// Pass `api = null` to represent "nothing selected" (renders empty). Any feature
// (searchroom chat, a future DM/messages page) reuses this by supplying its own
// adapter.
export default function useChatTranscript(api) {
  const [messages, setMessages] = useState([])
  const [loading, setLoading] = useState(false)
  const [loadingOlder, setLoadingOlder] = useState(false)
  const [loadingNewer, setLoadingNewer] = useState(false)
  const [hasMoreBefore, setHasMoreBefore] = useState(false)
  const [hasMoreAfter, setHasMoreAfter] = useState(false)
  const [highlightId, setHighlightId] = useState(null)
  const [error, setError] = useState('')

  // Guards against out-of-order full reloads (reset/jump) when switching fast.
  const reqRef = useRef(0)

  const firstRealId = useCallback(() => {
    const m = messages.find((x) => !x.pending)
    return m ? m._id : null
  }, [messages])

  const lastRealId = useCallback(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (!messages[i].pending) return messages[i]._id
    }
    return null
  }, [messages])

  // Load the latest page (used on mount, room switch, and "jump to latest").
  const reset = useCallback(async () => {
    if (!api) {
      setMessages([])
      setHasMoreBefore(false)
      setHasMoreAfter(false)
      return
    }
    const token = (reqRef.current += 1)
    setLoading(true)
    setError('')
    setHighlightId(null)
    try {
      const page = await api.listPage({})
      if (token !== reqRef.current) return
      setMessages(page.messages)
      setHasMoreBefore(page.hasMoreBefore)
      setHasMoreAfter(page.hasMoreAfter)
    } catch (err) {
      if (token === reqRef.current) setError(err.message || 'Failed to load messages')
    } finally {
      if (token === reqRef.current) setLoading(false)
    }
  }, [api])

  useEffect(() => {
    reset()
  }, [reset])

  const loadOlder = useCallback(async () => {
    if (!api || loadingOlder || !hasMoreBefore) return
    const before = firstRealId()
    if (!before) return
    setLoadingOlder(true)
    try {
      const page = await api.listPage({ before })
      setMessages((prev) => [...page.messages, ...prev])
      setHasMoreBefore(page.hasMoreBefore)
    } catch (err) {
      setError(err.message || 'Failed to load earlier messages')
    } finally {
      setLoadingOlder(false)
    }
  }, [api, loadingOlder, hasMoreBefore, firstRealId])

  const loadNewer = useCallback(async () => {
    if (!api || loadingNewer || !hasMoreAfter) return
    const after = lastRealId()
    if (!after) return
    setLoadingNewer(true)
    try {
      const page = await api.listPage({ after })
      setMessages((prev) => [...prev, ...page.messages])
      setHasMoreAfter(page.hasMoreAfter)
    } catch (err) {
      setError(err.message || 'Failed to load newer messages')
    } finally {
      setLoadingNewer(false)
    }
  }, [api, loadingNewer, hasMoreAfter, lastRealId])

  // Replace the window with one centered on `id`, and mark it for highlight.
  const jumpTo = useCallback(
    async (id) => {
      if (!api) return
      const token = (reqRef.current += 1)
      setLoading(true)
      setError('')
      try {
        const page = await api.listPage({ around: id })
        if (token !== reqRef.current) return
        // Set messages, highlight, and loading:false together so the target node
        // is present in the same render the scroll-into-view effect runs on.
        setMessages(page.messages)
        setHasMoreBefore(page.hasMoreBefore)
        setHasMoreAfter(page.hasMoreAfter)
        setHighlightId(id)
        setLoading(false)
      } catch (err) {
        if (token === reqRef.current) {
          setError(err.message || 'Failed to open message')
          setLoading(false)
        }
      }
    },
    [api]
  )

  // Fade the jump highlight after a moment (re-jumping re-triggers it).
  useEffect(() => {
    if (!highlightId) return undefined
    const timer = setTimeout(() => setHighlightId(null), 2500)
    return () => clearTimeout(timer)
  }, [highlightId])

  // Optimistic-update helpers for features that send messages.
  const appendLocal = useCallback((msg) => setMessages((prev) => [...prev, msg]), [])
  const replaceLocal = useCallback(
    (tempId, msgs) =>
      setMessages((prev) => [...prev.filter((m) => m._id !== tempId), ...msgs]),
    []
  )
  const removeLocal = useCallback(
    (tempId) => setMessages((prev) => prev.filter((m) => m._id !== tempId)),
    []
  )

  return {
    messages,
    loading,
    loadingOlder,
    loadingNewer,
    hasMoreBefore,
    hasMoreAfter,
    highlightId,
    error,
    reset,
    loadOlder,
    loadNewer,
    jumpTo,
    appendLocal,
    replaceLocal,
    removeLocal,
    setError,
  }
}
